import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./admin.module.scss";
import cx from "./cx";
import Icon from "./Icon";

export type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: React.ReactNode;
}

export interface ToastApi {
  show: (message: React.ReactNode, tone?: ToastTone) => void;
  success: (message: React.ReactNode) => void;
  error: (message: React.ReactNode) => void;
  info: (message: React.ReactNode) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 4000,
  error: 7000,
};

interface ToastViewProps {
  tone?: ToastTone;
  onClose?: () => void;
  children: React.ReactNode;
}

/** One toast. Usually shown through `useToast()`. */
export const Toast: React.FC<ToastViewProps> = ({
  tone = "success",
  onClose,
  children,
}) => (
  <div className={styles.toast} role={tone === "error" ? "alert" : "status"}>
    <span
      className={cx(
        styles.toastIcon,
        tone === "error" && styles.toastIconError,
        tone === "info" && styles.toastIconInfo,
      )}
      aria-hidden="true"
    >
      <Icon
        name={tone === "error" ? "x" : tone === "info" ? "info" : "check"}
        size={14}
        strokeWidth={2.6}
      />
    </span>
    <span>{children}</span>
    {onClose && (
      <button
        type="button"
        className={styles.toastClose}
        onClick={onClose}
        aria-label="Lukk"
      >
        <Icon name="x" size={14} strokeWidth={2.2} />
      </button>
    )}
  </div>
);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: React.ReactNode, tone: ToastTone = "success") => {
      const id = nextId.current++;
      setItems((list) => [...list.slice(-2), { id, tone, message }]);
      window.setTimeout(() => dismiss(id), DURATION[tone]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m) => show(m, "success"),
      error: (m) => show(m, "error"),
      info: (m) => show(m, "info"),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.toastStack} aria-live="polite">
        {items.map((t) => (
          <Toast key={t.id} tone={t.tone} onClose={() => dismiss(t.id)}>
            {t.message}
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};
